import React from 'react'
import ReactHtmlParser from 'react-html-parser'
import Navigation from './Navigation'
import Topbar from './Topbar'
import styled from 'styled-components'
import Search from './Search'
import Link from 'next/link'
import { useRouter } from 'next/router'
import getConfig from 'next/config'
import { breakpoint } from './styles'

const Header = ({ header, translations }: any) => {
  const router = useRouter()

  const [offset, setOffset] = React.useState<boolean>(false)
  const [searchOpen, setSearchOpen] = React.useState<boolean>(false)
  const [menuOpen, setMenuOpen] = React.useState<boolean>(false)
  const [megamenu, setMegamenu] = React.useState(null)
  const el = React.useRef(null)
  const { publicRuntimeConfig } = getConfig()

  React.useEffect(() => {
    const handleScroll = () => {
      document.body.classList[window.pageYOffset > 140 ? 'add' : 'remove']('is-scrolling')
      setOffset(window.pageYOffset > 100)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, []);

  React.useEffect(() => {
    if (menuOpen)
      setSearchOpen(false)
  }, [menuOpen]);

  React.useEffect(() => {
    if (searchOpen) {
      setMenuOpen(false)
      setMegamenu(null)
    }
  }, [searchOpen]);

  // Close megamenu and mobile menu on route change so dropdowns
  // don't persist after client-side navigation.
  React.useEffect(() => {
    const handleRouteChange = () => {
      setMegamenu(null)
      setMenuOpen(false)
    }
    router.events.on('routeChangeComplete', handleRouteChange)
    return () => router.events.off('routeChangeComplete', handleRouteChange)
  }, [router.events])

  // header can be null during a CMS outage with no stale cache — render nothing
  // rather than crashing on header.logo.url, header.title, header.navigation_menu etc.
  if (!header) return null

  return (
    <>
      {header?.notification_bar?.show_announcement &&
        <Notification>
          {ReactHtmlParser(header.notification_bar.announcement_text)}
        </Notification>}
      <HeaderContainer offset={offset.toString()} ref={el}>
        <Content>
          <Link href="/" passHref>
            <Logo offset={offset.toString()} aria-label={`${header.title} - Home`}>
              <img
                src={`${header.logo.url}?auto=webp&format=png`}
                alt=""
                title={header.title}
                width={98}
                height={88} />
            </Logo>
          </Link>
          <NavContainer>
            {publicRuntimeConfig?.dropdownLanguageSelector &&
              <Topbar {...{ searchOpen, setSearchOpen, setMenuOpen }} />}
            <Navigation {...{
              navigation_menu: header.navigation_menu,
              searchOpen,
              setSearchOpen,
              menuOpen,
              setMenuOpen,
              megamenu,
              setMegamenu
            }} />
          </NavContainer>
        </Content>
        {searchOpen &&
          <Search {...{ setSearchOpen, translations }} path="/products" />}
      </HeaderContainer>
      {searchOpen &&
        <Overlay onClick={() => setSearchOpen(false)}></Overlay>}
    </>
  )
}

export default Header

const Overlay = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  background: rgba(0,0,0,0.6);
  z-index: 3;
`

const NavContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`

const Notification = styled.div`
  background: var(--notification-bg);
  &, & a {
    color: var(--notification-text);
  }
  padding: 0.5rem 0;
  font-size: 12px;
  text-transform: uppercase;
  text-align: center;
  position: relative;
  z-index: 4;
  p {
    padding: 0;
    margin: 0;
  }
  @media print {
    display: none
  }
`

export const Logo = styled.a<any>`
  cursor: pointer;
  transition: padding 350ms ease;;
  padding: 10px 0;
  @media ${breakpoint.desktop} {
    padding: 1rem 0;
  }
  & > div {
    display: block !important;
  }
  & > img {
    transition: max-width 350ms ease;
    display: block;
    object-fit: contain;
    height: 40px;
    @media ${breakpoint.desktop} {
      height: 56px;
    }
  }
`

export const Content = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  @media print {
    justify-content: center;
  }
`

export const HeaderContainer = styled.header<any>`
  position: sticky;
  top: 0;
  z-index: 4;
  transition: background-color 500ms ease;
  background-color: var(--background);
  border-bottom: 2px rgba(102, 102, 102, 0.3) solid;
  width: 100%;
  padding: 0 1.5rem;
  @media ${breakpoint.desktop} {
    padding: 0;
  }
  @media print {
    border-bottom: 0;
    position: static;
  }
`
